import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchableUserSelect from "@/components/SearchableUserSelect";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, ArrowLeft, CheckCircle, Clock, TennisBall, ArrowCounterClockwise, Trash, PencilSimple, ShieldCheck, Play, Pause, X as XIcon, User as UserIcon } from "@phosphor-icons/react";
import { CATEGORIES } from "@/lib/locations";
import EventMediaGallery from "@/components/EventMediaGallery";
import InteractiveScorePanel from "@/components/InteractiveScorePanel";
import { getAvatarUrl, getInitials } from "@/lib/avatar";
import BestPairs from "@/components/BestPairs";
import { emitStatsChanged, useStatsBus } from "@/lib/statsBus";

const WIN_COLOR = "#34C759";   // green — victoria / valor mayor
const LOSS_COLOR = "#FF3B30";  // red — derrota

const statusColors = {
  en_curso: "bg-[#CCFF00]/20 text-[#CCFF00] border-[#CCFF00]/30",
  pendiente_validacion: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  validado: "bg-green-500/20 text-green-400 border-green-500/30",
  finalizado: "bg-blue-500/20 text-blue-400 border-blue-500/30",
};
const statusLabel = { en_curso: "EN VIVO", pendiente_validacion: "POR VALIDAR", validado: "VALIDADO", finalizado: "TERMINADO" };

function setsMatchSummary(m) {
  const s1 = m.sets.filter((s) => s.winner === 1).length;
  const s2 = m.sets.filter((s) => s.winner === 2).length;
  return `${s1}-${s2}`;
}

// League-style points: win clean=3, win w/ set lost=2, loss w/ set won=1, sweep loss=0
function leaguePointsFor(m, pairKey) {
  const side = pairKey === "pair1" ? 1 : 2;
  const setsW = m.sets.filter((s) => s.winner === side).length;
  const setsL = m.sets.filter((s) => s.winner && s.winner !== side).length;
  const isWin = m.winner_pair === side;
  if (!m.winner_pair) return null;
  if (isWin) return setsL === 0 ? 3 : 2;
  return setsW === 0 ? 0 : 1;
}

function fmtSeconds(total) {
  const s = Math.max(0, Math.floor(total || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

/** Chronometer card shown on match detail. Updates every second locally; syncs with backend state. */
function MatchChronometer({ match, canEdit, onAction, role }) {
  const chrono = match.chronometer || { started_at: null, paused_elapsed_seconds: 0, is_running: false };
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!chrono.is_running) return;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [chrono.is_running]);
  const elapsed = (() => {
    const base = chrono.paused_elapsed_seconds || 0;
    if (chrono.is_running && chrono.started_at) {
      const started = new Date(chrono.started_at).getTime();
      return base + Math.floor((Date.now() - started) / 1000);
    }
    return base;
  })();
  // reference `tick` to keep re-render tied to interval
  void tick;
  const canSetManual = role === "admin" || role === "directivo";
  const onSet = () => {
    const v = window.prompt("Tiempo en minutos (admin/directivo)", String(Math.floor(elapsed / 60)));
    if (v == null) return;
    const mins = parseInt(v, 10);
    if (Number.isFinite(mins) && mins >= 0) onAction("set", mins * 60);
  };
  return (
    <div className="card-tactical p-4 flex flex-wrap items-center gap-3" data-testid="chronometer-card">
      <div>
        <p className="text-[10px] uppercase tracking-widest text-gray-500">Cronometro</p>
        <p className="font-['Barlow_Condensed'] font-black text-3xl sm:text-4xl text-white font-mono" data-testid="chrono-display">{fmtSeconds(elapsed)}</p>
      </div>
      {canEdit && (
        <div className="flex items-center gap-2 ml-auto flex-wrap">
          {chrono.is_running ? (
            <Button onClick={() => onAction("pause")} variant="outline" className="border-orange-500/30 text-orange-400 hover:bg-orange-500/10 uppercase text-xs" data-testid="chrono-pause"><Pause size={14} className="mr-1" /> Pausar</Button>
          ) : (
            <Button onClick={() => onAction("start")} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase text-xs" data-testid="chrono-start"><Play size={14} className="mr-1" /> {elapsed > 0 ? "Reanudar" : "Iniciar"}</Button>
          )}
          <Button onClick={() => onAction("reset")} variant="outline" className="border-white/10 text-gray-400 hover:bg-white/5 uppercase text-xs" data-testid="chrono-reset"><ArrowCounterClockwise size={14} className="mr-1" /> Reiniciar</Button>
          {canSetManual && <Button onClick={onSet} variant="outline" className="border-[#CCFF00]/30 text-[#CCFF00] hover:bg-[#CCFF00]/10 uppercase text-xs" data-testid="chrono-set"><PencilSimple size={14} className="mr-1" /> Fijar</Button>}
        </div>
      )}
    </div>
  );
}

/** Matches list page (/dashboard/marcador) */
export function MatchesList() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const leagueIdParam = searchParams.get("league_id") || "";
  const tournamentIdParam = searchParams.get("tournament_id") || "";
  const [matches, setMatches] = useState([]);
  const [users, setUsers] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ p1a: "", p1an: "", p1b: "", p1bn: "", p2a: "", p2an: "", p2b: "", p2bn: "", club_id: "", court_name: "", category: "", date: new Date().toISOString().slice(0, 10), time: "", format: "standard" });
  const [filter, setFilter] = useState(() => {
    try { return localStorage.getItem("marcador.filter") || "all"; } catch { return "all"; }
  });
  const [search, setSearch] = useState(() => {
    try { return localStorage.getItem("marcador.search") || ""; } catch { return ""; }
  });
  const [retaDayFilter, setRetaDayFilter] = useState(() => {
    try { return localStorage.getItem("marcador.retaDay") || "all"; } catch { return "all"; }
  });
  // Player autocomplete + selected player card
  const [selectedPlayer, setSelectedPlayer] = useState(() => {
    try {
      const raw = localStorage.getItem("marcador.selectedPlayer");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [playerCard, setPlayerCard] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Persist filters
  useEffect(() => { try { localStorage.setItem("marcador.filter", filter); } catch {} }, [filter]);
  useEffect(() => { try { localStorage.setItem("marcador.search", search); } catch {} }, [search]);
  useEffect(() => { try { localStorage.setItem("marcador.retaDay", retaDayFilter); } catch {} }, [retaDayFilter]);
  useEffect(() => {
    try {
      if (selectedPlayer) localStorage.setItem("marcador.selectedPlayer", JSON.stringify(selectedPlayer));
      else localStorage.removeItem("marcador.selectedPlayer");
    } catch {}
  }, [selectedPlayer]);

  // Load public player card when a player is selected
  useEffect(() => {
    if (!selectedPlayer?.id) { setPlayerCard(null); return; }
    api.get(`/public/player/${selectedPlayer.id}`)
      .then(({ data }) => setPlayerCard(data))
      .catch(() => setPlayerCard(null));
  }, [selectedPlayer?.id]);

  // Pair-ranking data when filtered by a league (shows dupla rankings, not individual)
  const [pairRanking, setPairRanking] = useState(null);
  useEffect(() => {
    if (!leagueIdParam) { setPairRanking(null); return; }
    api.get(`/leagues/${leagueIdParam}/ranking-pairs`)
      .then(({ data }) => setPairRanking(data))
      .catch(() => setPairRanking(null));
  }, [leagueIdParam, matches.length]);

  const load = useCallback(() => {
    setLoading(true);
    const limit = user?.role === "admin" ? 100000 : 1000;
    const params = new URLSearchParams();
    params.set("limit", String(limit));
    if (leagueIdParam) params.set("league_id", leagueIdParam);
    if (tournamentIdParam) params.set("tournament_id", tournamentIdParam);
    api.get(`/matches?${params.toString()}`).then(({ data }) => setMatches(data)).catch(() => {}).finally(() => setLoading(false));
  }, [user?.role, leagueIdParam, tournamentIdParam]);

  useEffect(() => {
    load();
    api.get("/users").then(({ data }) => setUsers(data.filter((u) => u.role === "jugador" || u.role === "profesor"))).catch(() => {});
    api.get("/clubs").then(({ data }) => setClubs(data)).catch(() => {});
    api.get("/tournaments").then(({ data }) => setTournaments(data || [])).catch(() => {});
    api.get("/leagues").then(({ data }) => setLeagues(data || [])).catch(() => {});
    const t = setInterval(load, 15000); // refresh every 15s for live scores
    return () => clearInterval(t);
  }, [load]);
  useStatsBus(() => load());

  const tournamentsById = useMemo(() => Object.fromEntries(tournaments.map((t) => [t.id, t])), [tournaments]);
  const leaguesById = useMemo(() => Object.fromEntries(leagues.map((l) => [l.id, l])), [leagues]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return matches.filter((m) => {
      if (filter !== "all" && m.status !== filter) return false;
      if (retaDayFilter !== "all" && m.reta_day !== retaDayFilter) return false;
      // If a specific player is selected → match by user_id in any of the 4 slots
      if (selectedPlayer?.id) {
        const ids = [m.pair1?.p1_id, m.pair1?.p2_id, m.pair2?.p1_id, m.pair2?.p2_id];
        if (!ids.includes(selectedPlayer.id)) return false;
      }
      if (!q) return true;
      const names = [
        m.pair1?.p1_name, m.pair1?.p2_name, m.pair2?.p1_name, m.pair2?.p2_name,
      ].filter(Boolean).map((s) => s.toLowerCase());
      const tournament = m.tournament_id ? tournamentsById[m.tournament_id]?.name?.toLowerCase() || "" : "";
      const league = m.league_id ? leaguesById[m.league_id]?.name?.toLowerCase() || "" : "";
      const retaDay = (m.reta_day || "").toLowerCase();
      const isReta = !!m.reta_id;
      const haystack = [...names, tournament, league, retaDay, isReta ? "reta" : "", m.tournament_id ? "torneo" : "", m.rental_id ? "renta" : "", m.category || ""].join(" ");
      return haystack.includes(q);
    });
  }, [matches, filter, search, retaDayFilter, selectedPlayer, tournamentsById, leaguesById]);

  // Autocomplete suggestions: registered players whose name starts with / contains the query
  const playerSuggestions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length < 2) return [];
    return users
      .filter((u) => (u.name || "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [search, users]);

  const userOrName = (id, nameFallback) => {
    if (id === "__name__") return nameFallback;
    const u = users.find((x) => x.id === id);
    return u ? { id: u.id, name: u.name } : { name: nameFallback || "Jugador" };
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        pair1_p1: form.p1a === "__name__" ? { name: form.p1an } : { id: form.p1a, name: users.find((u) => u.id === form.p1a)?.name },
        pair1_p2: form.p1b === "__name__" ? { name: form.p1bn } : { id: form.p1b, name: users.find((u) => u.id === form.p1b)?.name },
        pair2_p1: form.p2a === "__name__" ? { name: form.p2an } : { id: form.p2a, name: users.find((u) => u.id === form.p2a)?.name },
        pair2_p2: form.p2b === "__name__" ? { name: form.p2bn } : { id: form.p2b, name: users.find((u) => u.id === form.p2b)?.name },
        club_id: form.club_id || null,
        court_name: form.court_name || null,
        category: form.category || null,
        date: form.date || null,
        time: form.time || null,
        format: form.format || "standard",
      };
      const { data } = await api.post("/matches", payload);
      toast.success("Partido creado");
      emitStatsChanged("match:created", { matchId: data?.id });
      setDialog(false);
      load();
      return data;
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const PlayerSelect = ({ valueId, valueName, onSelectId, onName, label }) => (
    <div>
      <Label className="text-[10px] tracking-widest uppercase text-gray-500 mb-1 block">{label}</Label>
      <SearchableUserSelect
        value={valueId === "__name__" ? "" : valueId}
        onChange={(id) => onSelectId(id || "")}
        options={users}
        placeholder="Selecciona jugador..."
        testId={`player-select-${label?.toLowerCase?.() || ""}`}
        allowClear
      />
      <button type="button" onClick={() => onSelectId("__name__")} className="text-[10px] uppercase tracking-widest text-gray-400 hover:text-[#CCFF00] mt-1">o escribir nombre manualmente ›</button>
      {valueId === "__name__" && <Input placeholder="Nombre completo" value={valueName} onChange={(e) => onName(e.target.value)} className="mt-2 bg-[#0A0A0A] border-white/20 text-white h-10" />}
    </div>
  );

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="matches-list-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Marcador</p>
          <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">Partidos en Vivo</h1>
          <p className="text-sm text-gray-400 mt-1">Sets a 6 juegos, puntaje tenis 15/30/40/AD, mejor de 3</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={load} variant="outline" size="sm" className="border-white/10 text-gray-400 hover:text-[#CCFF00] hover:border-[#CCFF00]/30 uppercase text-xs" data-testid="refresh-matches-btn">
            <ArrowCounterClockwise size={14} className="mr-1" /> Actualizar
          </Button>
          <Button onClick={() => setDialog(true)} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase text-xs" data-testid="new-match-btn">
            <Plus size={16} className="mr-2" /> Nuevo partido
          </Button>
        </div>
      </div>

      {/* Cross-link tiles to related dashboards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="matches-cross-links">
        <Link to="/dashboard/torneos" className="bg-purple-500/10 border border-purple-500/30 hover:bg-purple-500/20 text-purple-300 px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center justify-between" data-testid="link-torneos">
          <span>Torneos</span><span className="font-['JetBrains_Mono'] text-purple-200">{tournaments.length}</span>
        </Link>
        <Link to="/dashboard/retas" className="bg-orange-500/10 border border-orange-500/30 hover:bg-orange-500/20 text-orange-300 px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center justify-between" data-testid="link-retas">
          <span>Retas</span><span className="font-['JetBrains_Mono'] text-orange-200">→</span>
        </Link>
        <Link to="/dashboard/ligas" className="bg-amber-500/10 border border-amber-500/30 hover:bg-amber-500/20 text-amber-300 px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center justify-between" data-testid="link-ligas">
          <span>Ligas</span><span className="font-['JetBrains_Mono'] text-amber-200">{leagues.length}</span>
        </Link>
        <Link to="/dashboard/estadisticas" className="bg-[#CCFF00]/10 border border-[#CCFF00]/30 hover:bg-[#CCFF00]/20 text-[#CCFF00] px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center justify-between" data-testid="link-stats">
          <span>Ranking</span><span className="font-['JetBrains_Mono']">→</span>
        </Link>
      </div>

      {/* Search bar */}
      <div className="card-tactical p-4" data-testid="match-search-bar">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex-1 min-w-[260px] relative">
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setShowSuggestions(true); }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              placeholder="Buscar por jugador, torneo, reta o liga…"
              className="bg-[#0A0A0A] border-white/10 text-white pl-9 h-10"
              data-testid="match-search-input"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-xs">🔎</span>
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white text-xs" data-testid="match-search-clear">✕</button>
            )}

            {/* Autocomplete dropdown */}
            {showSuggestions && playerSuggestions.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-1 bg-[#141414] border border-white/10 shadow-xl z-20 max-h-72 overflow-y-auto" data-testid="player-suggestions">
                {playerSuggestions.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setSelectedPlayer({ id: u.id, name: u.name });
                      setSearch("");
                      setShowSuggestions(false);
                    }}
                    className="w-full flex items-center gap-3 p-2.5 hover:bg-[#CCFF00]/10 text-left border-b border-white/5 last:border-0"
                    data-testid={`player-suggestion-${u.id}`}
                  >
                    <img
                      src={getAvatarUrl(u, 48)}
                      alt={u.name}
                      className="w-9 h-9 rounded-full border border-white/10 shrink-0 object-cover bg-[#0A0A0A]"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-white font-semibold truncate">{u.name}</p>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider">
                        {u.role}{u.category ? ` · Cat. ${u.category}` : ""}{u.club_id ? " · Club" : ""}
                      </p>
                    </div>
                    <UserIcon size={14} className="text-[#CCFF00] shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <select value={retaDayFilter} onChange={(e) => setRetaDayFilter(e.target.value)} className="bg-[#0A0A0A] border border-white/10 text-white text-xs px-3 h-10 cursor-pointer hover:border-[#CCFF00]/30" data-testid="match-retaday-filter">
            <option value="all">Día de reta: cualquiera</option>
            <option value="lunes">Lunes</option>
            <option value="miercoles">Miércoles</option>
            <option value="viernes">Viernes</option>
            <option value="domingo">Domingo</option>
          </select>
          {(search || retaDayFilter !== "all" || filter !== "all" || selectedPlayer) && (
            <button
              onClick={() => { setSearch(""); setRetaDayFilter("all"); setFilter("all"); setSelectedPlayer(null); }}
              className="text-[10px] uppercase tracking-widest text-gray-400 hover:text-[#CCFF00] border border-white/10 hover:border-[#CCFF00]/30 px-3 h-10 font-bold"
              data-testid="match-clear-filters"
            >
              Limpiar filtros
            </button>
          )}
          <span className="text-[10px] uppercase tracking-widest text-gray-500" data-testid="match-results-count">{filtered.length} de {matches.length}</span>
        </div>

        {/* Selected player pill */}
        {selectedPlayer && (
          <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="selected-player-pill">
            <span className="text-[10px] uppercase tracking-widest text-gray-500">Filtrando por jugador:</span>
            <Badge variant="outline" className="bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/30 text-[11px] font-semibold">
              {selectedPlayer.name}
            </Badge>
            <button
              onClick={() => setSelectedPlayer(null)}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-gray-500 hover:text-red-400"
              data-testid="clear-selected-player"
            >
              <XIcon size={10} /> quitar
            </button>
          </div>
        )}

        {/* League / Tournament filter pill (from URL query) */}
        {(leagueIdParam || tournamentIdParam) && (
          <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="scope-filter-pill">
            {leagueIdParam && (
              <>
                <span className="text-[10px] uppercase tracking-widest text-amber-400">🏆 Filtrando por liga:</span>
                <Badge variant="outline" className="bg-amber-500/10 text-amber-300 border-amber-500/30 text-[11px] font-semibold" data-testid="league-filter-badge">
                  {leaguesById[leagueIdParam]?.name || leagueIdParam}
                </Badge>
              </>
            )}
            {tournamentIdParam && !leagueIdParam && (
              <>
                <span className="text-[10px] uppercase tracking-widest text-purple-400">🏆 Filtrando por torneo:</span>
                <Badge variant="outline" className="bg-purple-500/10 text-purple-300 border-purple-500/30 text-[11px] font-semibold" data-testid="tournament-filter-badge">
                  {tournamentsById[tournamentIdParam]?.name || tournamentIdParam}
                </Badge>
              </>
            )}
            <button
              onClick={() => { const p = new URLSearchParams(searchParams); p.delete("league_id"); p.delete("tournament_id"); setSearchParams(p); }}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-gray-500 hover:text-red-400"
              data-testid="clear-scope-filter"
            >
              <XIcon size={10} /> quitar filtro
            </button>
          </div>
        )}
      </div>

      {/* Ranking POR DUPLA — only when filtered by a league (per user request) */}
      {leagueIdParam && pairRanking && (
        <div className="card-tactical p-5 border-amber-500/30" data-testid="league-pair-ranking">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <p className="text-[10px] tracking-[0.2em] uppercase text-amber-400 font-semibold">Ranking por dupla</p>
              <h3 className="font-['Barlow_Condensed'] font-black text-xl uppercase text-white">
                {leaguesById[leagueIdParam]?.name || "Liga"}
              </h3>
            </div>
            <div className="flex items-center gap-2 text-[10px] text-gray-500 uppercase tracking-widest font-['JetBrains_Mono']">
              <span data-testid="pair-ranking-count">{pairRanking.ranking?.length || 0} duplas</span>
              <span>·</span>
              <span>{pairRanking.matches_counted || 0} partidos</span>
            </div>
          </div>
          {(!pairRanking.ranking || pairRanking.ranking.length === 0) ? (
            <p className="text-xs text-gray-500">Aún no hay duplas con partidos validados en esta liga.</p>
          ) : (
            <div className="grid grid-cols-[36px_1fr_60px_60px_60px] gap-2 text-[9px] uppercase tracking-widest text-gray-600 pb-1 border-b border-white/5">
              <span>#</span>
              <span>Dupla</span>
              <span className="text-center">Part.</span>
              <span className="text-center">Vict.</span>
              <span className="text-center text-amber-400">Pts</span>
            </div>
          )}
          <div className="max-h-64 overflow-y-auto" data-testid="pair-ranking-list">
            {(pairRanking.ranking || []).map((r, i) => {
              const podium = i === 0 ? { c: "#FFD700", lbl: "🥇" }
                : i === 1 ? { c: "#C0C0C0", lbl: "🥈" }
                : i === 2 ? { c: "#CD7F32", lbl: "🥉" }
                : null;
              return (
                <div
                  key={r.pair_key || `${r.display_name}-${i}`}
                  className="grid grid-cols-[36px_1fr_60px_60px_60px] gap-2 items-center py-2 border-b border-white/5 last:border-b-0"
                  data-testid={`pair-rank-${i}`}
                >
                  <span className="font-['Barlow_Condensed'] font-black text-lg text-center" style={{ color: podium?.c || "#4B5563" }}>
                    {podium ? podium.lbl : `#${i + 1}`}
                  </span>
                  <div className="text-sm text-white truncate flex items-center gap-1" title={r.display_name}>
                    <span className="truncate">{r.p1_name || (r.display_name || "").split(" / ")[0]}</span>
                    <span className="text-[#CCFF00] font-black shrink-0" aria-hidden="true">·</span>
                    <span className="truncate">{r.p2_name || (r.display_name || "").split(" / ")[1] || ""}</span>
                  </div>
                  <span className="text-xs text-center text-gray-400">{r.matches}</span>
                  <span className="text-xs text-center text-gray-400">{r.wins}</span>
                  <span className="font-['Barlow_Condensed'] font-black text-lg text-center text-amber-400">{r.points}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Player Card (when a player is selected) */}
      {selectedPlayer && playerCard && (
        <div className="card-tactical p-5 border-[#CCFF00]/30" data-testid="player-card">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="relative shrink-0">
              {playerCard.photo_path ? (
                <img
                  src={getAvatarUrl({ photo_path: playerCard.photo_path, name: playerCard.name }, 128)}
                  alt={playerCard.name}
                  className="w-20 h-20 rounded-full border-2 border-[#CCFF00] object-cover bg-[#0A0A0A]"
                />
              ) : (
                <img
                  src={getAvatarUrl({ name: playerCard.name, id: playerCard.id }, 128)}
                  alt={playerCard.name}
                  className="w-20 h-20 rounded-full border-2 border-[#CCFF00] bg-[#0A0A0A]"
                />
              )}
              <span className="absolute -bottom-1 -right-1 bg-[#CCFF00] text-black text-[10px] font-bold px-1.5 py-0.5 rounded-full">#{playerCard.stats?.ranking || "-"}</span>
            </div>
            <div className="flex-1 min-w-[200px]">
              <p className="text-[10px] tracking-[0.2em] uppercase text-[#CCFF00] font-semibold mb-0.5">Jugador</p>
              <h2 className="font-['Barlow_Condensed'] font-black text-2xl sm:text-3xl uppercase tracking-tight text-white" data-testid="player-card-name">{playerCard.name || selectedPlayer.name || getInitials(selectedPlayer.name)}</h2>
              <div className="flex items-center gap-2 flex-wrap mt-1">
                {playerCard.category && <Badge variant="outline" className="bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/30 text-[10px]">Cat. {playerCard.category}</Badge>}
                {playerCard.role && <Badge variant="outline" className="bg-white/5 text-gray-400 border-white/10 text-[10px] uppercase">{playerCard.role}</Badge>}
                {playerCard.club && <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/30 text-[10px]">🏢 {playerCard.club}</Badge>}
                {(playerCard.state || playerCard.country) && <Badge variant="outline" className="bg-white/5 text-gray-400 border-white/10 text-[10px]">📍 {[playerCard.state, playerCard.country].filter(Boolean).join(", ")}</Badge>}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center min-w-[260px]">
              <div>
                <p className="text-[9px] uppercase tracking-widest text-gray-500">Puntos</p>
                <p className="font-['Barlow_Condensed'] font-black text-2xl text-[#CCFF00]" data-testid="player-card-points">{playerCard.stats?.points || 0}</p>
              </div>
              <div>
                <p className="text-[9px] uppercase tracking-widest text-gray-500">Partidos</p>
                <p className="font-['Barlow_Condensed'] font-black text-2xl text-white" data-testid="player-card-games">{playerCard.validated_matches_count || 0}</p>
              </div>
              <div>
                <p className="text-[9px] uppercase tracking-widest text-gray-500">Ranking</p>
                <p className="font-['Barlow_Condensed'] font-black text-2xl" style={{ color: WIN_COLOR }} data-testid="player-card-ranking">#{playerCard.stats?.ranking || "-"}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-4 flex-wrap">
            <Link
              to={`/player/${playerCard.id}`}
              className="inline-flex items-center gap-1.5 bg-[#CCFF00] text-black font-bold uppercase text-[11px] tracking-wider px-3 h-9 hover:bg-[#B3E600]"
              data-testid="player-card-profile-link"
            >
              <UserIcon size={12} /> Ver perfil público
            </Link>
            {playerCard.top_rivals?.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-widest text-gray-500">Top rivales:</span>
                {playerCard.top_rivals.map((r) => (
                  <Badge key={r.name} variant="outline" className="bg-orange-500/10 text-orange-300 border-orange-500/30 text-[10px]">
                    {r.name} · {r.count}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Best duplas widget for the selected player card */}
      {playerCard?.id && (
        <BestPairs
          userId={playerCard.id}
          userName={playerCard.name}
          defaultPeriod="week"
          testid="marcadores-best-pairs"
        />
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {[{ k: "all", l: "Todos" }, { k: "en_curso", l: "En vivo" }, { k: "pendiente_validacion", l: "Por validar" }, { k: "validado", l: "Validados" }].map((f) => (
          <button key={f.k} onClick={() => setFilter(f.k)} className={`px-3 h-9 text-xs font-semibold uppercase tracking-wider border transition-colors ${filter === f.k ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-[#0A0A0A] text-gray-400 border-white/20 hover:border-[#CCFF00]/30"}`} data-testid={`filter-${f.k}`}>{f.l}</button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="card-tactical p-12 text-center">
          <TennisBall size={48} weight="duotone" className="text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">Sin partidos</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((m) => {
            const s1 = m.sets.filter((s) => s.winner === 1).length;
            const s2 = m.sets.filter((s) => s.winner === 2).length;
            const g1 = m.sets.reduce((a, s) => a + (s.pair1_games || 0), 0) + (m.status === "en_curso" ? (m.current_set_games?.pair1 || 0) : 0);
            const g2 = m.sets.reduce((a, s) => a + (s.pair2_games || 0), 0) + (m.status === "en_curso" ? (m.current_set_games?.pair2 || 0) : 0);
            const score1 = m.is_tiebreak ? (m.tiebreak_points?.pair1 ?? 0) : (m.current_game?.pair1 ?? "0");
            const score2 = m.is_tiebreak ? (m.tiebreak_points?.pair2 ?? 0) : (m.current_game?.pair2 ?? "0");
            const setDetail = m.sets.map((s) => `${s.pair1_games}-${s.pair2_games}${s.tiebreak ? ` (${s.tiebreak.pair1}-${s.tiebreak.pair2})` : ""}`).join(", ");
            return (
              <Link to={`/dashboard/marcador/${m.id}`} key={m.id} className="card-tactical p-5 hover:border-[#CCFF00]/40 transition-colors" data-testid={`match-card-${m.id}`}>
                <div className="flex items-start justify-between mb-3 gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className={`badge-status ${statusColors[m.status]}`}>{statusLabel[m.status] || m.status}</Badge>
                    {m.category && <Badge variant="outline" className="bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/30 text-[10px]">Cat. {m.category}</Badge>}
                    <Badge variant="outline" className="bg-white/5 text-gray-400 border-white/10 text-[10px]">{m.format === "short" ? "Tabla 4-0" : "2 de 3 sets"}</Badge>
                    {m.tournament_id && <Badge variant="outline" className="bg-purple-500/10 text-purple-400 border-purple-500/30 text-[10px]" data-testid={`origin-torneo-${m.id}`}>Torneo</Badge>}
                    {m.reta_id && <Badge variant="outline" className="bg-orange-500/10 text-orange-400 border-orange-500/30 text-[10px]" data-testid={`origin-reta-${m.id}`}>Reta</Badge>}
                    {m.rental_id && <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/30 text-[10px]" data-testid={`origin-renta-${m.id}`}>Renta</Badge>}
                    {!m.tournament_id && !m.reta_id && !m.rental_id && <Badge variant="outline" className="bg-white/5 text-gray-500 border-white/10 text-[10px]">Casual</Badge>}
                    {m.league_id && <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/30 text-[10px]" data-testid={`league-badge-${m.id}`}>🏆 Liga</Badge>}
                    {m.reta_day && <Badge variant="outline" className="bg-cyan-500/10 text-cyan-400 border-cyan-500/30 text-[10px]" data-testid={`retaday-badge-${m.id}`}>Reta {m.reta_day}</Badge>}
                    {m.court_name && <Badge variant="outline" className="bg-blue-500/10 text-blue-300 border-blue-500/30 text-[10px]" data-testid={`court-badge-${m.id}`}>🎾 {m.court_name}</Badge>}
                  </div>
                  <span className="text-[10px] text-gray-600 font-['JetBrains_Mono']">{m.date}{m.time ? ` · ${m.time}` : ""}</span>
                </div>

                {/* Origin name line */}
                {(m.tournament_id || m.league_id) && (
                  <p className="text-[11px] text-gray-400 mb-2 truncate" data-testid={`origin-name-${m.id}`}>
                    {m.tournament_id && tournamentsById[m.tournament_id]?.name && <span className="text-purple-300">{tournamentsById[m.tournament_id].name}</span>}
                    {m.tournament_id && m.league_id && <span className="text-gray-600"> · </span>}
                    {m.league_id && leaguesById[m.league_id]?.name && <span className="text-amber-300">{leaguesById[m.league_id].name}</span>}
                  </p>
                )}

                {/* Header labels — Sets | Juegos | Pts Liga | Score */}
                <div className="grid grid-cols-[1fr_42px_42px_50px_50px] gap-2 text-[9px] uppercase tracking-widest text-gray-600 pb-1 border-b border-white/5">
                  <span>Pareja</span>
                  <span className="text-center" title="Sets ganados">Sets</span>
                  <span className="text-center" title="Juegos totales">Juegos</span>
                  <span className="text-center" title="Puntos Liga (3/2/1/0)">Pts</span>
                  <span className="text-center" title="Puntaje del juego actual">Score</span>
                </div>
                {/* Pair rows */}
                {[["pair1", m.pair1], ["pair2", m.pair2]].map(([k, pair]) => {
                  const isW = m.winner_pair === (k === "pair1" ? 1 : 2);
                  const isFinal = m.status === "validado" || m.status === "finalizado" || m.status === "pendiente_validacion";
                  const setsW = k === "pair1" ? s1 : s2;
                  const gamesW = k === "pair1" ? g1 : g2;
                  const scoreW = k === "pair1" ? score1 : score2;
                  const ligaPts = leaguePointsFor(m, k);
                  const nameColor = !isFinal ? "text-white" : (isW ? "" : "");
                  const nameStyle = isFinal ? { color: isW ? WIN_COLOR : LOSS_COLOR } : {};
                  const numColor = !isFinal ? "text-gray-300" : (isW ? "" : "");
                  const numStyle = isFinal ? { color: isW ? WIN_COLOR : LOSS_COLOR } : {};
                  return (
                    <div key={k} className="grid grid-cols-[1fr_42px_42px_50px_50px] gap-2 items-center py-2 border-b border-white/5 last:border-0">
                      <p className={`font-semibold truncate text-sm ${nameColor}`} style={nameStyle}>{pair.p1_name} / {pair.p2_name}</p>
                      <span className={`font-['Barlow_Condensed'] font-black text-2xl text-center tabular-nums ${numColor}`} style={numStyle} data-testid={`${k}-sets-${m.id}`}>{setsW}</span>
                      <span className={`font-['Barlow_Condensed'] font-black text-2xl text-center tabular-nums ${numColor}`} style={numStyle} data-testid={`${k}-games-${m.id}`}>{gamesW}</span>
                      <span className="text-center font-['JetBrains_Mono'] text-sm font-bold tabular-nums" style={{ color: isFinal ? (ligaPts === 3 ? WIN_COLOR : ligaPts === 0 ? LOSS_COLOR : "#FFF") : "#666" }} data-testid={`${k}-ligapts-${m.id}`}>
                        {ligaPts != null ? ligaPts : "-"}
                      </span>
                      <span className="text-center font-['Barlow_Condensed'] font-black text-2xl text-white bg-white/5 border border-white/10 px-1" data-testid={`${k}-score-${m.id}`}>{scoreW}</span>
                    </div>
                  );
                })}
                {setDetail && <p className="font-['JetBrains_Mono'] text-[11px] text-gray-500 mt-3 pt-3 border-t border-white/5" data-testid={`match-setscores-${m.id}`}>Sets: {setDetail} · Resultado {setsMatchSummary(m)}</p>}
                {m.is_tiebreak && <p className="text-[10px] uppercase tracking-widest text-orange-400 font-bold mt-1">Super tiebreak</p>}
              </Link>
            );
          })}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-match-dialog">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl font-bold uppercase">Nuevo Partido</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-widest font-semibold mb-2">Formato</p>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setForm(p => ({ ...p, format: "standard" }))} className={`p-3 text-left border transition-colors ${form.format === "standard" ? "bg-[#CCFF00]/10 border-[#CCFF00] text-[#CCFF00]" : "bg-[#0A0A0A] border-white/20 text-gray-300 hover:border-[#CCFF00]/30"}`} data-testid="format-standard">
                  <p className="font-bold text-sm uppercase tracking-wider">2 de 3 sets</p>
                  <p className="text-[11px] text-gray-500 mt-0.5">Sets a 6 juegos · TB 7-6</p>
                </button>
                <button type="button" onClick={() => setForm(p => ({ ...p, format: "short" }))} className={`p-3 text-left border transition-colors ${form.format === "short" ? "bg-[#CCFF00]/10 border-[#CCFF00] text-[#CCFF00]" : "bg-[#0A0A0A] border-white/20 text-gray-300 hover:border-[#CCFF00]/30"}`} data-testid="format-short">
                  <p className="font-bold text-sm uppercase tracking-wider">Tabla 4-0</p>
                  <p className="text-[11px] text-gray-500 mt-0.5">Sets a 4 · TB a 7 en 3-3</p>
                </button>
              </div>
            </div>
            <div>
              <p className="text-xs text-[#CCFF00] uppercase tracking-widest font-semibold mb-2">Pareja 1</p>
              <div className="grid grid-cols-2 gap-3">
                <PlayerSelect valueId={form.p1a} valueName={form.p1an} onSelectId={(v) => setForm(p => ({ ...p, p1a: v }))} onName={(v) => setForm(p => ({ ...p, p1an: v }))} label="Jugador 1" />
                <PlayerSelect valueId={form.p1b} valueName={form.p1bn} onSelectId={(v) => setForm(p => ({ ...p, p1b: v }))} onName={(v) => setForm(p => ({ ...p, p1bn: v }))} label="Jugador 2" />
              </div>
            </div>
            <div>
              <p className="text-xs text-orange-400 uppercase tracking-widest font-semibold mb-2">Pareja 2</p>
              <div className="grid grid-cols-2 gap-3">
                <PlayerSelect valueId={form.p2a} valueName={form.p2an} onSelectId={(v) => setForm(p => ({ ...p, p2a: v }))} onName={(v) => setForm(p => ({ ...p, p2an: v }))} label="Jugador 3" />
                <PlayerSelect valueId={form.p2b} valueName={form.p2bn} onSelectId={(v) => setForm(p => ({ ...p, p2b: v }))} onName={(v) => setForm(p => ({ ...p, p2bn: v }))} label="Jugador 4" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] tracking-widest uppercase text-gray-500 mb-1 block">Club</Label>
                <Select value={form.club_id} onValueChange={(v) => setForm(p => ({ ...p, club_id: v, court_name: "" }))}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-10" data-testid="new-match-club"><SelectValue placeholder="Selecciona club" /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10 max-h-[260px]">
                    {clubs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] tracking-widest uppercase text-gray-500 mb-1 block">Cancha</Label>
                {(() => {
                  const club = clubs.find((c) => c.id === form.club_id);
                  const courts = (club?.court_names && club.court_names.length > 0)
                    ? club.court_names
                    : (club ? Array.from({ length: club.courts_count || 0 }, (_, i) => `Cancha ${i + 1}`) : []);
                  return (
                    <Select value={form.court_name || "__none"} onValueChange={(v) => setForm(p => ({ ...p, court_name: v === "__none" ? "" : v }))} disabled={!form.club_id || courts.length === 0}>
                      <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-10" data-testid="new-match-court">
                        <SelectValue placeholder={!form.club_id ? "Elige club primero" : courts.length === 0 ? "Sin canchas" : "Selecciona cancha"} />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-white/10 max-h-[260px]">
                        <SelectItem value="__none">Sin asignar</SelectItem>
                        {courts.map((cn) => <SelectItem key={cn} value={cn}>{cn}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  );
                })()}
              </div>
              <div>
                <Label className="text-[10px] tracking-widest uppercase text-gray-500 mb-1 block">Categoria</Label>
                <Select value={form.category} onValueChange={(v) => setForm(p => ({ ...p, category: v }))}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-10" data-testid="new-match-category"><SelectValue placeholder="Sin cat." /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10 max-h-[260px]">
                    {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-[10px] tracking-widest uppercase text-gray-500 mb-1 block">Fecha</Label><Input type="date" value={form.date} onChange={(e) => setForm(p => ({ ...p, date: e.target.value }))} className="bg-[#0A0A0A] border-white/20 text-white h-10" data-testid="new-match-date" /></div>
              <div><Label className="text-[10px] tracking-widest uppercase text-gray-500 mb-1 block">Hora</Label><Input type="time" value={form.time} onChange={(e) => setForm(p => ({ ...p, time: e.target.value }))} className="bg-[#0A0A0A] border-white/20 text-white h-10" data-testid="new-match-time" /></div>
            </div>
            <DialogFooter><Button type="submit" className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase w-full">Crear partido</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Match detail / live scoreboard (/dashboard/marcador/:id) */
export function MatchDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [context, setContext] = useState({ tournament: null, reta: null, rental: null, league: null, club: null });

  const load = useCallback(() => {
    api.get(`/matches/${id}`).then(({ data }) => setMatch(data)).catch(() => toast.error("Error cargando partido")).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    load();
    api.get("/users").then(({ data }) => setAllUsers((data || []).filter((u) => u.role === "jugador" || u.role === "profesor"))).catch(() => {});
    api.get("/leagues").then(({ data }) => setLeagues(data || [])).catch(() => {});
    api.get("/clubs").then(({ data }) => setClubs(data || [])).catch(() => {});
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  // Fetch linked event context (tournament/reta/rental/league/club) once match is loaded
  useEffect(() => {
    if (!match) return;
    const tasks = [];
    if (match.tournament_id) tasks.push(api.get(`/tournaments/${match.tournament_id}`).then(r => ({ kind: "tournament", data: r.data })).catch(() => null));
    if (match.reta_id) tasks.push(api.get(`/retas/${match.reta_id}`).then(r => ({ kind: "reta", data: r.data })).catch(() => null));
    if (match.rental_id) tasks.push(api.get(`/court-rentals/${match.rental_id}`).then(r => ({ kind: "rental", data: r.data })).catch(() => null));
    if (match.league_id) tasks.push(api.get(`/leagues`).then(r => ({ kind: "league", data: (r.data || []).find(l => l.id === match.league_id) })).catch(() => null));
    if (match.club_id && clubs.length) {
      const cb = clubs.find(c => c.id === match.club_id);
      if (cb) setContext((s) => ({ ...s, club: cb }));
    }
    Promise.all(tasks).then((results) => {
      const next = { tournament: null, reta: null, rental: null, league: null, club: context.club };
      results.forEach(r => { if (r && r.data) next[r.kind] = r.data; });
      setContext((s) => ({ ...s, ...next }));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match?.tournament_id, match?.reta_id, match?.rental_id, match?.league_id, match?.club_id, clubs.length]);

  const canEdit = match && (user?.role === "admin" || (user?.role === "directivo" && user.club_id === match.club_id) || match.created_by === user?.id || [match.pair1.p1_id, match.pair1.p2_id, match.pair2.p1_id, match.pair2.p2_id].includes(user?.id));
  const canValidate = match && (user?.role === "admin" || (user?.role === "directivo" && user.club_id === match.club_id));
  const myPair = match && [match.pair1.p1_id, match.pair1.p2_id].includes(user?.id) ? 1 : (match && [match.pair2.p1_id, match.pair2.p2_id].includes(user?.id) ? 2 : null);

  const addPoint = async (pair) => {
    try { await api.post(`/matches/${id}/point`, { pair }); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const undo = async () => {
    try { await api.post(`/matches/${id}/undo-point`); toast.success("Juego actual reiniciado"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const chrono = async (action, seconds) => {
    try { await api.post(`/matches/${id}/chronometer`, { action, seconds }); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error cronometro"); }
  };
  const confirmResult = async () => {
    try { await api.post(`/matches/${id}/confirm`); toast.success("Resultado confirmado"); emitStatsChanged("match:confirmed", { matchId: id }); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const validate = async () => {
    try { await api.post(`/matches/${id}/validate`); toast.success("Resultado validado"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const del = async () => {
    if (!window.confirm("Eliminar partido?")) return;
    try { await api.delete(`/matches/${id}`); toast.success("Eliminado"); emitStatsChanged("match:deleted", { matchId: id }); nav("/dashboard/marcador"); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const openEdit = () => {
    setEditForm({
      p1_p1_name: match.pair1.p1_name, p1_p1_id: match.pair1.p1_id || "",
      p1_p2_name: match.pair1.p2_name, p1_p2_id: match.pair1.p2_id || "",
      p2_p1_name: match.pair2.p1_name, p2_p1_id: match.pair2.p1_id || "",
      p2_p2_name: match.pair2.p2_name, p2_p2_id: match.pair2.p2_id || "",
      sets: JSON.parse(JSON.stringify(match.sets || [])),
      cur_p1: match.current_set_games?.pair1 ?? 0,
      cur_p2: match.current_set_games?.pair2 ?? 0,
      g_p1: match.current_game?.pair1 ?? "0",
      g_p2: match.current_game?.pair2 ?? "0",
      status: match.status,
      league_id: match.league_id || "",
      reta_day: match.reta_day || "",
      category: match.category || "",
      club_id: match.club_id || "",
      court_name: match.court_name || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      const resolveUser = (currentId, currentName) => {
        if (currentId && currentId !== "__manual__") {
          const u = allUsers.find((x) => x.id === currentId);
          return { id: currentId, name: u?.name || currentName };
        }
        return { id: null, name: currentName };
      };
      const p1a = resolveUser(editForm.p1_p1_id, editForm.p1_p1_name);
      const p1b = resolveUser(editForm.p1_p2_id, editForm.p1_p2_name);
      const p2a = resolveUser(editForm.p2_p1_id, editForm.p2_p1_name);
      const p2b = resolveUser(editForm.p2_p2_id, editForm.p2_p2_name);
      const payload = {
        pair1: { ...match.pair1, p1_id: p1a.id, p1_name: p1a.name, p2_id: p1b.id, p2_name: p1b.name },
        pair2: { ...match.pair2, p1_id: p2a.id, p1_name: p2a.name, p2_id: p2b.id, p2_name: p2b.name },
        sets: editForm.sets.map((s) => ({ pair1_games: Number(s.pair1_games) || 0, pair2_games: Number(s.pair2_games) || 0, winner: Number(s.winner) || (Number(s.pair1_games) > Number(s.pair2_games) ? 1 : 2) })),
        current_set_games: { pair1: Number(editForm.cur_p1) || 0, pair2: Number(editForm.cur_p2) || 0 },
        current_game: { ...match.current_game, pair1: String(editForm.g_p1), pair2: String(editForm.g_p2) },
        status: editForm.status,
        league_id: editForm.league_id || null,
        reta_day: editForm.reta_day || null,
        category: editForm.category || null,
        club_id: editForm.club_id || null,
        court_name: editForm.court_name || null,
      };
      const s1 = payload.sets.filter((s) => s.winner === 1).length;
      const s2 = payload.sets.filter((s) => s.winner === 2).length;
      if (s1 === 2 || s2 === 2) {
        payload.winner_pair = s1 === 2 ? 1 : 2;
        if (payload.status === "en_curso") payload.status = "pendiente_validacion";
      }
      await api.put(`/matches/admin/${id}`, payload);
      toast.success("Partido actualizado y vinculado a jugadores");
      emitStatsChanged("match:admin-updated", { matchId: id });
      setEditOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
  };

  const setSetVal = (idx, field, v) => setEditForm((f) => { const sets = [...f.sets]; sets[idx] = { ...sets[idx], [field]: v }; return { ...f, sets }; });
  const addSet = () => setEditForm((f) => ({ ...f, sets: [...f.sets, { pair1_games: 0, pair2_games: 0, winner: 1 }] }));
  const rmSet = (i) => setEditForm((f) => ({ ...f, sets: f.sets.filter((_, idx) => idx !== i) }));

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>;
  if (!match) return <div className="card-tactical p-12 text-center"><p className="text-gray-400">No encontrado</p></div>;

  const s1 = match.sets.filter((s) => s.winner === 1).length;
  const s2 = match.sets.filter((s) => s.winner === 2).length;
  const g1 = match.sets.reduce((a, s) => a + (s.pair1_games || 0), 0) + (match.status === "en_curso" ? (match.current_set_games?.pair1 || 0) : 0);
  const g2 = match.sets.reduce((a, s) => a + (s.pair2_games || 0), 0) + (match.status === "en_curso" ? (match.current_set_games?.pair2 || 0) : 0);
  const ligaPts1 = leaguePointsFor(match, "pair1");
  const ligaPts2 = leaguePointsFor(match, "pair2");
  const isFinal = match.status === "validado" || match.status === "finalizado" || match.status === "pendiente_validacion";
  const win1 = match.winner_pair === 1;
  const win2 = match.winner_pair === 2;
  const nameClr = (isWin) => isFinal ? (isWin ? WIN_COLOR : LOSS_COLOR) : "#FFFFFF";
  const fmt = match.format === "short" ? { label: "Tabla 4-0", sub: "Sets a 4 · TB 3-3 a 7" } : { label: "2 de 3 sets", sub: "Sets a 6 · TB 7-6" };
  const isTb = !!match.is_tiebreak;
  const tbPts = match.tiebreak_points || { pair1: 0, pair2: 0 };
  const pair1Score = isTb ? tbPts.pair1 : match.current_game?.pair1;
  const pair2Score = isTb ? tbPts.pair2 : match.current_game?.pair2;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="match-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Link to="/dashboard/marcador" className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-gray-400 hover:text-[#CCFF00]"><ArrowLeft size={14} /> Volver</Link>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className={`badge-status ${statusColors[match.status]}`}>{statusLabel[match.status] || match.status}</Badge>
          {match.category && <Badge variant="outline" className="bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/30 text-[10px]">Cat. {match.category}</Badge>}
          <EventMediaGallery entityType="matches" entityId={match.id} testIdPrefix="match-media" />
          {(user?.role === "admin" || (user?.role === "directivo" && user.club_id === match.club_id) || (["profesor", "jugador"].includes(user?.role) && (match.created_by === user.id || [match.pair1.p1_id, match.pair1.p2_id, match.pair2.p1_id, match.pair2.p2_id].includes(user.id)))) && (
            <Button onClick={openEdit} variant="outline" size="sm" className="border-[#CCFF00]/30 text-[#CCFF00] hover:bg-[#CCFF00]/10 uppercase text-xs" data-testid="edit-match-btn">
              <PencilSimple size={14} className="mr-1" /> Editar
            </Button>
          )}
        </div>
      </div>

      {/* Event Context Header: what tournament/reta/rental/league/club this match belongs to */}
      {(context.tournament || context.reta || context.rental || context.league || context.club) && (
        <div className="card-tactical p-4" data-testid="match-context-header">
          <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-2">Contexto del partido</p>
          <div className="flex flex-wrap gap-2">
            {context.tournament && (
              <Link to="/dashboard/torneos" className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/30 text-purple-300 px-3 py-1.5 text-xs hover:bg-purple-500/20" data-testid="ctx-tournament">
                <span className="text-[9px] uppercase tracking-widest font-bold text-purple-400">Torneo</span>
                <span className="font-semibold">{context.tournament.name}</span>
                {context.tournament.start_date && <span className="text-[10px] text-purple-400/70">· {context.tournament.start_date}</span>}
              </Link>
            )}
            {context.reta && (
              <Link to="/dashboard/retas" className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 text-orange-300 px-3 py-1.5 text-xs hover:bg-orange-500/20" data-testid="ctx-reta">
                <span className="text-[9px] uppercase tracking-widest font-bold text-orange-400">Reta</span>
                <span className="font-semibold">{context.reta.date}{context.reta.time ? ` · ${context.reta.time}` : ""}</span>
                {match.reta_day && <span className="text-[10px] text-orange-400/70">· {match.reta_day}</span>}
              </Link>
            )}
            {context.rental && (
              <Link to="/dashboard/canchas" className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 text-blue-300 px-3 py-1.5 text-xs hover:bg-blue-500/20" data-testid="ctx-rental">
                <span className="text-[9px] uppercase tracking-widest font-bold text-blue-400">Renta</span>
                <span className="font-semibold">{context.rental.date} · {context.rental.time_slot}</span>
              </Link>
            )}
            {context.league && (
              <Link to="/dashboard/ligas" className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 text-amber-300 px-3 py-1.5 text-xs hover:bg-amber-500/20" data-testid="ctx-league">
                <span className="text-[9px] uppercase tracking-widest font-bold text-amber-400">Liga</span>
                <span className="font-semibold">{context.league.name}</span>
              </Link>
            )}
            {context.club && (
              <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 text-gray-300 px-3 py-1.5 text-xs" data-testid="ctx-club">
                <span className="text-[9px] uppercase tracking-widest font-bold text-gray-400">Club</span>
                <span className="font-semibold">{context.club.name}</span>
                {match.court_name && <span className="text-[10px] text-blue-400 ml-1">🎾 {match.court_name}</span>}
              </div>
            )}
            {!context.club && match.court_name && (
              <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 text-blue-300 px-3 py-1.5 text-xs" data-testid="ctx-court">
                <span className="text-[9px] uppercase tracking-widest font-bold text-blue-400">Cancha</span>
                <span className="font-semibold">🎾 {match.court_name}</span>
              </div>
            )}
            {!context.tournament && !context.reta && !context.rental && (
              <Badge variant="outline" className="bg-white/5 text-gray-500 border-white/10 text-[10px]" data-testid="ctx-casual">Partido casual</Badge>
            )}
          </div>
        </div>
      )}

      {/* Big scoreboard */}
      <div className="card-tactical p-6 sm:p-8" data-testid="scoreboard">
        <div className="grid grid-cols-[1fr_auto_auto] gap-4 items-center">
          {/* Pair 1 */}
          <div>
            <p className="text-[10px] tracking-widest uppercase font-semibold mb-1" style={{ color: nameClr(win1) }}>Pareja 1 {isFinal && win1 ? "· VICTORIA" : isFinal && !win1 ? "· DERROTA" : ""}</p>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[9px] uppercase tracking-widest text-[#CCFF00]/60 font-bold bg-[#CCFF00]/10 border border-[#CCFF00]/30 px-1.5 py-0.5" data-testid="pair1-p1-side">Drive</span>
              <p className="text-lg sm:text-xl font-bold" style={{ color: nameClr(win1) }} data-testid="pair1-p1-name">{match.pair1.p1_name}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap mt-1">
              <span className="text-[9px] uppercase tracking-widest text-[#CCFF00]/60 font-bold bg-[#CCFF00]/10 border border-[#CCFF00]/30 px-1.5 py-0.5" data-testid="pair1-p2-side">Revés</span>
              <p className="text-lg sm:text-xl font-bold" style={{ color: nameClr(win1) }} data-testid="pair1-p2-name">{match.pair1.p2_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {match.sets.map((s, i) => <span key={i} className="font-['Barlow_Condensed'] font-black text-3xl sm:text-5xl tabular-nums" style={{ color: s.winner === 1 ? WIN_COLOR : LOSS_COLOR }}>{s.pair1_games}</span>)}
            {match.status === "en_curso" && <span className="font-['Barlow_Condensed'] font-black text-3xl sm:text-5xl text-orange-400">{match.current_set_games.pair1}</span>}
          </div>
          <div className="min-w-[72px] text-center bg-white/5 border border-white/10 px-2 py-2">
            {isTb && <p className="text-[9px] uppercase text-orange-400 tracking-widest">TB</p>}
            <p className="font-['Barlow_Condensed'] font-black text-3xl sm:text-5xl text-white" data-testid="pair1-current">{pair1Score}</p>
          </div>
          {/* Pair 2 */}
          <div className="mt-4">
            <p className="text-[10px] tracking-widest uppercase font-semibold mb-1" style={{ color: nameClr(win2) }}>Pareja 2 {isFinal && win2 ? "· VICTORIA" : isFinal && !win2 ? "· DERROTA" : ""}</p>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[9px] uppercase tracking-widest text-orange-400/70 font-bold bg-orange-500/10 border border-orange-500/30 px-1.5 py-0.5" data-testid="pair2-p1-side">Drive</span>
              <p className="text-lg sm:text-xl font-bold" style={{ color: nameClr(win2) }} data-testid="pair2-p1-name">{match.pair2.p1_name}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap mt-1">
              <span className="text-[9px] uppercase tracking-widest text-orange-400/70 font-bold bg-orange-500/10 border border-orange-500/30 px-1.5 py-0.5" data-testid="pair2-p2-side">Revés</span>
              <p className="text-lg sm:text-xl font-bold" style={{ color: nameClr(win2) }} data-testid="pair2-p2-name">{match.pair2.p2_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-4">
            {match.sets.map((s, i) => <span key={i} className="font-['Barlow_Condensed'] font-black text-3xl sm:text-5xl tabular-nums" style={{ color: s.winner === 2 ? WIN_COLOR : LOSS_COLOR }}>{s.pair2_games}</span>)}
            {match.status === "en_curso" && <span className="font-['Barlow_Condensed'] font-black text-3xl sm:text-5xl text-orange-400">{match.current_set_games.pair2}</span>}
          </div>
          <div className="mt-4 min-w-[72px] text-center bg-white/5 border border-white/10 px-2 py-2">
            {isTb && <p className="text-[9px] uppercase text-orange-400 tracking-widest">TB</p>}
            <p className="font-['Barlow_Condensed'] font-black text-3xl sm:text-5xl text-white" data-testid="pair2-current">{pair2Score}</p>
          </div>
        </div>
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-5 gap-2 text-center text-[10px] uppercase tracking-widest border-t border-white/5 pt-3" data-testid="scoreboard-totals">
          <div>
            <p className="text-gray-500">Sets</p>
            <p className="font-['Barlow_Condensed'] font-black text-xl tabular-nums">
              <span style={{ color: isFinal ? (win1 ? WIN_COLOR : LOSS_COLOR) : "#FFF" }}>{s1}</span>
              <span className="text-gray-600 mx-1">·</span>
              <span style={{ color: isFinal ? (win2 ? WIN_COLOR : LOSS_COLOR) : "#FFF" }}>{s2}</span>
            </p>
          </div>
          <div>
            <p className="text-gray-500">Juegos</p>
            <p className="font-['Barlow_Condensed'] font-black text-xl tabular-nums">
              <span style={{ color: isFinal ? (win1 ? WIN_COLOR : LOSS_COLOR) : "#FFF" }}>{g1}</span>
              <span className="text-gray-600 mx-1">·</span>
              <span style={{ color: isFinal ? (win2 ? WIN_COLOR : LOSS_COLOR) : "#FFF" }}>{g2}</span>
            </p>
          </div>
          <div>
            <p className="text-gray-500">Pts Liga</p>
            <p className="font-['Barlow_Condensed'] font-black text-xl tabular-nums">
              <span style={{ color: ligaPts1 === 3 ? WIN_COLOR : ligaPts1 === 0 ? LOSS_COLOR : "#FFF" }}>{ligaPts1 ?? "-"}</span>
              <span className="text-gray-600 mx-1">·</span>
              <span style={{ color: ligaPts2 === 3 ? WIN_COLOR : ligaPts2 === 0 ? LOSS_COLOR : "#FFF" }}>{ligaPts2 ?? "-"}</span>
            </p>
          </div>
          <div>
            <p className="text-gray-500">Formato</p>
            <p className="text-white font-semibold" data-testid="format-label">{fmt.label}</p>
          </div>
          <div className="col-span-2 sm:col-span-1">
            <p className="text-gray-500">Reglas</p>
            <p className="text-gray-400">{fmt.sub}</p>
          </div>
        </div>
        {isTb && <p className="mt-2 text-center text-[11px] uppercase tracking-widest text-orange-400 font-bold" data-testid="tiebreak-indicator">Super tiebreak en curso — primero a 7 con margen de 2</p>}
      </div>

      <MatchChronometer match={match} canEdit={canEdit} onAction={chrono} role={user?.role} />

      {/* Interactive tennis ladder + inline meta editor */}
      <InteractiveScorePanel match={match} canEdit={canEdit} onChanged={load} leagues={leagues} clubs={clubs} allUsers={allUsers} />

      {/* Point controls */}
      {canEdit && match.status === "en_curso" && (
        <div className="space-y-2" data-testid="point-controls">
          {myPair && <p className="text-[10px] uppercase tracking-widest text-[#CCFF00] font-semibold">Tu perteneces a Pareja {myPair}</p>}
          <div className="grid grid-cols-2 gap-3">
            <Button onClick={() => addPoint(1)} className={`${myPair === 1 ? "ring-2 ring-[#CCFF00]" : ""} bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase h-16 text-lg`} data-testid="point-pair1">+1 Pareja 1</Button>
            <Button onClick={() => addPoint(2)} className={`${myPair === 2 ? "ring-2 ring-[#CCFF00]" : ""} bg-orange-500 text-black hover:bg-orange-400 font-bold uppercase h-16 text-lg`} data-testid="point-pair2">+1 Pareja 2</Button>
          </div>
          <Button onClick={undo} variant="outline" className="w-full border-white/10 text-gray-400 hover:bg-white/5 uppercase text-xs" data-testid="undo-btn">
            <ArrowCounterClockwise size={14} className="mr-1" /> Reiniciar juego actual (0/0)
          </Button>
        </div>
      )}

      {/* Confirmations */}
      {match.status !== "en_curso" && (
        <div className="card-tactical p-5" data-testid="confirmations-section">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-3">Validacion del resultado</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div className={`p-3 border ${match.confirmations.pair1_confirmed ? "bg-green-500/10 border-green-500/30 text-green-400" : "bg-white/5 border-white/10 text-gray-400"}`}>
              {match.confirmations.pair1_confirmed ? <CheckCircle size={14} className="inline mr-1" /> : <Clock size={14} className="inline mr-1" />} Pareja 1 {match.confirmations.pair1_confirmed ? "confirmo" : "pendiente"}
            </div>
            <div className={`p-3 border ${match.confirmations.pair2_confirmed ? "bg-green-500/10 border-green-500/30 text-green-400" : "bg-white/5 border-white/10 text-gray-400"}`}>
              {match.confirmations.pair2_confirmed ? <CheckCircle size={14} className="inline mr-1" /> : <Clock size={14} className="inline mr-1" />} Pareja 2 {match.confirmations.pair2_confirmed ? "confirmo" : "pendiente"}
            </div>
            <div className={`p-3 border ${match.confirmations.directivo_validated ? "bg-green-500/10 border-green-500/30 text-green-400" : "bg-white/5 border-white/10 text-gray-400"}`}>
              {match.confirmations.directivo_validated ? <ShieldCheck size={14} className="inline mr-1" /> : <Clock size={14} className="inline mr-1" />} Directivo {match.confirmations.directivo_validated ? "valido" : "pendiente"}
            </div>
          </div>
          <div className="flex items-center gap-2 mt-4 flex-wrap">
            {myPair && !match.confirmations[`pair${myPair}_confirmed`] && (
              <Button onClick={confirmResult} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase text-xs" data-testid="confirm-btn">
                <CheckCircle size={14} className="mr-1" /> Confirmo el resultado
              </Button>
            )}
            {canValidate && !match.confirmations.directivo_validated && (
              <Button onClick={validate} variant="outline" className="bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/30 hover:bg-[#CCFF00]/20 font-bold uppercase text-xs" data-testid="validate-btn">
                <ShieldCheck size={14} className="mr-1" /> Validar como directivo
              </Button>
            )}
            {(user?.role === "admin" || (user?.role === "directivo" && user.club_id === match.club_id)) && (
              <Button onClick={del} variant="outline" className="bg-red-500/10 text-red-400 border-red-500/30 hover:bg-red-500/20 uppercase text-xs ml-auto" data-testid="delete-match-btn">
                <Trash size={14} className="mr-1" /> Eliminar
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Admin edit dialog */}
      {editForm && (
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="bg-[#1A1A1A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-match-dialog">
            <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl font-bold uppercase">Editar Partido (Admin)</DialogTitle></DialogHeader>
            <form onSubmit={saveEdit} className="space-y-4">
              <div className="text-[11px] text-gray-500 bg-blue-500/5 border border-blue-500/20 p-2">
                Vincula cada jugador a su cuenta registrada para que el resultado aparezca en su perfil publico.
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { k: "p1_p1", label: "Pareja 1 - Jugador 1", color: "text-[#CCFF00]" },
                  { k: "p1_p2", label: "Pareja 1 - Jugador 2", color: "text-[#CCFF00]" },
                  { k: "p2_p1", label: "Pareja 2 - Jugador 3", color: "text-orange-400" },
                  { k: "p2_p2", label: "Pareja 2 - Jugador 4", color: "text-orange-400" },
                ].map((p) => (
                  <div key={p.k} className="space-y-1.5">
                    <Label className={`text-[10px] uppercase tracking-widest font-semibold ${p.color}`}>{p.label}</Label>
                    <SearchableUserSelect
                      value={editForm[`${p.k}_id`] || ""}
                      onChange={(id, u) => setEditForm((f) => ({ ...f, [`${p.k}_id`]: id, [`${p.k}_name`]: u?.name || f[`${p.k}_name`] }))}
                      options={allUsers}
                      placeholder="Seleccionar jugador"
                      allowClear
                      testId={`edit-${p.k}-select`}
                    />
                    <Input value={editForm[`${p.k}_name`]} onChange={(e) => setEditForm((f) => ({ ...f, [`${p.k}_name`]: e.target.value }))} placeholder="Nombre mostrado" className="bg-[#0A0A0A] border-white/20 text-white h-9 text-sm" data-testid={`edit-${p.k}-name`} />
                  </div>
                ))}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-400 uppercase tracking-widest font-semibold">Sets completados</p>
                  <Button type="button" onClick={addSet} variant="outline" size="sm" className="border-white/10 text-gray-400 hover:bg-white/5 uppercase text-xs h-7" data-testid="add-set-btn"><Plus size={12} className="mr-1" /> Agregar set</Button>
                </div>
                <div className="space-y-2">
                  {editForm.sets.length === 0 && <p className="text-xs text-gray-500 italic">Sin sets aun</p>}
                  {editForm.sets.map((s, i) => (
                    <div key={i} className="grid grid-cols-[auto_1fr_1fr_1fr_auto] gap-2 items-center" data-testid={`edit-set-row-${i}`}>
                      <span className="text-xs text-gray-500 font-['JetBrains_Mono'] w-12">Set {i + 1}</span>
                      <Input type="number" min="0" max="7" value={s.pair1_games} onChange={(e) => setSetVal(i, "pair1_games", e.target.value)} className="bg-[#0A0A0A] border-white/20 text-white h-9" placeholder="P1" />
                      <Input type="number" min="0" max="7" value={s.pair2_games} onChange={(e) => setSetVal(i, "pair2_games", e.target.value)} className="bg-[#0A0A0A] border-white/20 text-white h-9" placeholder="P2" />
                      <Select value={String(s.winner)} onValueChange={(v) => setSetVal(i, "winner", Number(v))}>
                        <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-9"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-white/10"><SelectItem value="1">Gano P1</SelectItem><SelectItem value="2">Gano P2</SelectItem></SelectContent>
                      </Select>
                      <Button type="button" onClick={() => rmSet(i)} variant="ghost" size="icon" className="text-red-400 hover:bg-red-500/10 h-9 w-9"><Trash size={14} /></Button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs text-gray-400 uppercase tracking-widest font-semibold mb-2">Set en curso (juegos)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[10px] text-gray-500">Pareja 1</Label><Input type="number" min="0" value={editForm.cur_p1} onChange={(e) => setEditForm((f) => ({ ...f, cur_p1: e.target.value }))} className="bg-[#0A0A0A] border-white/20 text-white h-10" data-testid="edit-cur-p1" /></div>
                  <div><Label className="text-[10px] text-gray-500">Pareja 2</Label><Input type="number" min="0" value={editForm.cur_p2} onChange={(e) => setEditForm((f) => ({ ...f, cur_p2: e.target.value }))} className="bg-[#0A0A0A] border-white/20 text-white h-10" data-testid="edit-cur-p2" /></div>
                </div>
              </div>

              <div>
                <p className="text-xs text-gray-400 uppercase tracking-widest font-semibold mb-2">Puntaje juego actual (15/30/40/AD)</p>
                <div className="grid grid-cols-2 gap-3">
                  <Select value={editForm.g_p1} onValueChange={(v) => setEditForm((f) => ({ ...f, g_p1: v }))}>
                    <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-10" data-testid="edit-game-p1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-white/10">{["0", "15", "30", "40", "AD"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={editForm.g_p2} onValueChange={(v) => setEditForm((f) => ({ ...f, g_p2: v }))}>
                    <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-10" data-testid="edit-game-p2"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-white/10">{["0", "15", "30", "40", "AD"].map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-[10px] text-gray-500 uppercase tracking-widest">Estado</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/20 text-white h-10" data-testid="edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    <SelectItem value="en_curso">En curso</SelectItem>
                    <SelectItem value="pendiente_validacion">Pendiente validacion</SelectItem>
                    <SelectItem value="validado">Validado</SelectItem>
                    <SelectItem value="finalizado">Finalizado</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="p-3 bg-[#0A0A0A] border border-white/5 space-y-3">
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#CCFF00] font-bold">Clasificacion</p>
                <div>
                  <Label className="text-[10px] text-gray-500 uppercase tracking-widest">Club</Label>
                  <Select value={editForm.club_id || "__none"} onValueChange={(v) => setEditForm((f) => ({ ...f, club_id: v === "__none" ? "" : v, court_name: "" }))}>
                    <SelectTrigger className="bg-[#141414] border-white/20 text-white h-10" data-testid="edit-match-club"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-white/10">
                      <SelectItem value="__none">Sin club</SelectItem>
                      {clubs.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-gray-500 uppercase tracking-widest">Cancha</Label>
                  {(() => {
                    const club = clubs.find((c) => c.id === editForm.club_id);
                    const courts = club?.court_names || [];
                    return (
                      <Select value={editForm.court_name || "__none"} onValueChange={(v) => setEditForm((f) => ({ ...f, court_name: v === "__none" ? "" : v }))} disabled={!editForm.club_id || courts.length === 0}>
                        <SelectTrigger className="bg-[#141414] border-white/20 text-white h-10" data-testid="edit-match-court">
                          <SelectValue placeholder={!editForm.club_id ? "Elige club primero" : courts.length === 0 ? "Sin canchas" : "Selecciona cancha"} />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-white/10 max-h-[260px]">
                          <SelectItem value="__none">Sin asignar</SelectItem>
                          {courts.map((cn) => <SelectItem key={cn} value={cn}>{cn}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    );
                  })()}
                </div>
                <div>
                  <Label className="text-[10px] text-gray-500 uppercase tracking-widest">Liga</Label>
                  <Select value={editForm.league_id || "__none"} onValueChange={(v) => setEditForm((f) => ({ ...f, league_id: v === "__none" ? "" : v }))}>
                    <SelectTrigger className="bg-[#141414] border-white/20 text-white h-10" data-testid="edit-match-league"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-white/10">
                      <SelectItem value="__none">Sin liga</SelectItem>
                      {leagues.map((lg) => (
                        <SelectItem key={lg.id} value={lg.id}>{lg.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-gray-500 uppercase tracking-widest">Categoria</Label>
                  <Select value={editForm.category || "__none"} onValueChange={(v) => setEditForm((f) => ({ ...f, category: v === "__none" ? "" : v }))}>
                    <SelectTrigger className="bg-[#141414] border-white/20 text-white h-10" data-testid="edit-match-category"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-white/10 max-h-[260px]">
                      <SelectItem value="__none">Sin categoria</SelectItem>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-gray-500 uppercase tracking-widest">Reta del dia</Label>
                  <Select value={editForm.reta_day || "__none"} onValueChange={(v) => setEditForm((f) => ({ ...f, reta_day: v === "__none" ? "" : v }))}>
                    <SelectTrigger className="bg-[#141414] border-white/20 text-white h-10" data-testid="edit-match-reta-day"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-white/10">
                      <SelectItem value="__none">Sin asignar</SelectItem>
                      <SelectItem value="lunes">Retas del Lunes</SelectItem>
                      <SelectItem value="miercoles">Retas del Miercoles</SelectItem>
                      <SelectItem value="viernes">Retas del Viernes</SelectItem>
                      <SelectItem value="domingo">Retas del Domingo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-[10px] text-gray-500">Los partidos con club + liga + categoria + reta del dia quedan separados en el ranking para agruparse por filtros.</p>
              </div>

              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setEditOpen(false)} className="border-white/10 text-gray-400 hover:bg-white/5 uppercase text-xs">Cancelar</Button>
                <Button type="submit" className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase text-xs" data-testid="save-edit-btn">Guardar cambios</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
